import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash2, Save, ChevronRight,
  Clock, AlertTriangle, Loader2, GitBranch, Shield, UserCheck, Pencil, X, Zap, Tag,
} from 'lucide-react'
import { workflowsApi, rolesApi } from '@/lib/api'
import { api } from '@/lib/api-client'
import type { Role } from '@/types'

// ── Local types ──────────────────────────────────────────────────────────────

interface LocalState {
  tempId: string
  serverId?: number        // set when loaded from server
  name: string
  color: string
  is_initial: boolean
  is_final: boolean
  requires_approval: boolean
  sla_hours: number | ''
  assigned_role_id: number | null   // role that approves in this state
}

interface LocalTransition {
  tempId: string
  serverId?: number
  from_temp_id: string
  to_temp_id: string
  action_name: string
  required_role_id: number | null
  auto_transition: boolean
  label_template_id: number | null
  label_required: boolean
}

// ── Constants ────────────────────────────────────────────────────────────────

const STATE_COLORS = [
  { value: '#6B7280', label: 'Cinzento' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#F59E0B', label: 'Amarelo' },
  { value: '#10B981', label: 'Verde' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#8B5CF6', label: 'Roxo' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#14B8A6', label: 'Ciano' },
]

let _seq = 0
const uid = () => `t${++_seq}_${Date.now()}`

// ── Component ────────────────────────────────────────────────────────────────

export default function WorkflowBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  // Workflow metadata
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [isActive, setIsActive] = useState(true)

  // States and transitions
  const [states, setStates] = useState<LocalState[]>([])
  const [transitions, setTransitions] = useState<LocalTransition[]>([])

  // Roles (for approver/permission selectors)
  const [roles, setRoles] = useState<Role[]>([])

  // Label templates (for label selector on transitions)
  const [labelTemplates, setLabelTemplates] = useState<Array<{id: number; name: string}>>([])

  // Add-state form
  const [showAddState, setShowAddState] = useState(false)
  const [ns, setNs] = useState({ name: '', color: '#6B7280', is_initial: false, is_final: false, requires_approval: false, sla_hours: '' as number | '', assigned_role_id: null as number | null })

  // Add-transition form
  const [showAddTrans, setShowAddTrans] = useState(false)
  const [nt, setNt] = useState({ from: '', to: '', action: '', required_role_id: null as number | null, auto_transition: false, label_template_id: null as number | null, label_required: false })

  // Inline-edit state form
  const [editingStateTempId, setEditingStateTempId] = useState<string | null>(null)
  const [es, setEs] = useState({ name: '', color: '#6B7280', is_final: false, requires_approval: false, sla_hours: '' as number | '', assigned_role_id: null as number | null })

  // Inline-edit transition form
  const [editingTransTempId, setEditingTransTempId] = useState<string | null>(null)
  const [et, setEt] = useState({ from: '', to: '', action: '', required_role_id: null as number | null, auto_transition: false, label_template_id: null as number | null, label_required: false })

  // UI
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  // ── Load existing workflow ─────────────────────────────────────────────────

  useEffect(() => {
    if (!isEdit || !id) return
    workflowsApi.get(Number(id))
      .then(res => {
        const wf = res.data
        setName(wf.name)
        setDescription(wf.description ?? '')
        setCategory(wf.category ?? '')
        setIsActive(wf.is_active)
        const ls: LocalState[] = (wf.states ?? []).map(s => ({
          tempId: uid(),
          serverId: s.id,
          name: s.name,
          color: s.color ?? '#6B7280',
          is_initial: s.is_initial,
          is_final: s.is_final,
          requires_approval: s.requires_approval,
          sla_hours: s.sla_hours ?? '',
          assigned_role_id: s.assigned_role_id ?? null,
        }))
        setStates(ls)
        setTransitions((wf.transitions ?? []).map(t => {
          const from = ls.find(s => s.serverId === t.from_state_id)
          const to   = ls.find(s => s.serverId === t.to_state_id)
          return { tempId: uid(), serverId: t.id, from_temp_id: from?.tempId ?? '', to_temp_id: to?.tempId ?? '', action_name: t.action_name, required_role_id: t.required_role_id ?? null, auto_transition: t.auto_transition ?? false, label_template_id: t.label_template_id ?? null, label_required: t.label_required ?? false }
        }))
      })
      .catch(() => setLoadError('Erro ao carregar workflow'))
      .finally(() => setLoading(false))
  }, [id, isEdit])

  // Fetch roles once
  useEffect(() => {
    rolesApi.list().then(res => setRoles(res.data)).catch(() => {})
  }, [])

  // Fetch label templates once
  useEffect(() => {
    api.get<Array<{id: number; name: string}>>('/api/settings/labels').then(res => setLabelTemplates(res.data)).catch(() => {})
  }, [])

  // ── State helpers ──────────────────────────────────────────────────────────

  function addState() {
    if (!ns.name.trim()) return
    const s: LocalState = { tempId: uid(), name: ns.name.trim(), color: ns.color, is_initial: ns.is_initial, is_final: ns.is_final, requires_approval: ns.requires_approval, sla_hours: ns.sla_hours, assigned_role_id: ns.assigned_role_id }
    setStates(prev => ns.is_initial ? prev.map(x => ({ ...x, is_initial: false })).concat(s) : [...prev, s])
    setNs({ name: '', color: '#6B7280', is_initial: false, is_final: false, requires_approval: false, sla_hours: '', assigned_role_id: null })
    setShowAddState(false)
  }

  async function handleRemoveState(tempId: string) {
    const s = states.find(x => x.tempId === tempId)
    if (!s) return
    if (s.serverId && isEdit) {
      try {
        await workflowsApi.deleteState(Number(id), s.serverId)
      } catch {
        setSaveError('Não foi possível eliminar o estado — pode ter instâncias ativas associadas.')
        return
      }
    }
    if (editingStateTempId === tempId) setEditingStateTempId(null)
    setStates(prev => prev.filter(x => x.tempId !== tempId))
    setTransitions(prev => prev.filter(t => t.from_temp_id !== tempId && t.to_temp_id !== tempId))
  }

  function startEditState(s: LocalState) {
    setShowAddState(false)
    setEditingStateTempId(s.tempId)
    setEs({ name: s.name, color: s.color, is_final: s.is_final, requires_approval: s.requires_approval, sla_hours: s.sla_hours, assigned_role_id: s.assigned_role_id })
  }

  async function saveStateEdit() {
    if (!editingStateTempId || !es.name.trim()) return
    const s = states.find(x => x.tempId === editingStateTempId)
    if (!s) return
    setSaveError(null)
    if (s.serverId && isEdit) {
      try {
        await workflowsApi.updateState(Number(id), s.serverId, {
          name: es.name.trim(),
          color: es.color,
          is_final: es.is_final,
          requires_approval: es.requires_approval,
          sla_hours: es.sla_hours !== '' ? Number(es.sla_hours) : undefined,
          assigned_role_id: es.assigned_role_id ?? undefined,
        })
      } catch {
        setSaveError('Erro ao guardar estado')
        return
      }
    }
    setStates(prev => prev.map(x =>
      x.tempId === editingStateTempId
        ? { ...x, name: es.name.trim(), color: es.color, is_final: es.is_final, requires_approval: es.requires_approval, sla_hours: es.sla_hours, assigned_role_id: es.assigned_role_id }
        : x
    ))
    setEditingStateTempId(null)
  }

  async function handleRemoveTransition(tempId: string) {
    const t = transitions.find(x => x.tempId === tempId)
    if (!t) return
    if (t.serverId && isEdit) {
      try {
        await workflowsApi.deleteTransition(Number(id), t.serverId)
      } catch {
        setSaveError('Não foi possível eliminar a transição')
        return
      }
    }
    if (editingTransTempId === tempId) setEditingTransTempId(null)
    setTransitions(prev => prev.filter(x => x.tempId !== tempId))
  }

  function startEditTrans(t: LocalTransition) {
    setShowAddTrans(false)
    setEditingTransTempId(t.tempId)
    setEt({ from: t.from_temp_id, to: t.to_temp_id, action: t.action_name, required_role_id: t.required_role_id, auto_transition: t.auto_transition, label_template_id: t.label_template_id, label_required: t.label_required })
  }

  async function saveTransEdit() {
    if (!editingTransTempId || !et.action.trim() || !et.from || !et.to) return
    const t = transitions.find(x => x.tempId === editingTransTempId)
    if (!t) return
    setSaveError(null)
    if (t.serverId && isEdit) {
      const fromS = states.find(s => s.tempId === et.from)
      const toS   = states.find(s => s.tempId === et.to)
      if (!fromS?.serverId || !toS?.serverId) { setSaveError('Guarda o workflow antes de editar transições entre estados novos'); return }
      try {
        await workflowsApi.updateTransition(Number(id), t.serverId, {
          from_state_id: fromS.serverId,
          to_state_id: toS.serverId,
          action_name: et.action.trim(),
          required_role_id: et.required_role_id ?? undefined,
          auto_transition: et.auto_transition,
          label_template_id: et.label_template_id,
          label_required: et.label_required,
        })
      } catch {
        setSaveError('Erro ao guardar transição')
        return
      }
    }
    setTransitions(prev => prev.map(x =>
      x.tempId === editingTransTempId
        ? { ...x, from_temp_id: et.from, to_temp_id: et.to, action_name: et.action.trim(), required_role_id: et.required_role_id, auto_transition: et.auto_transition, label_template_id: et.label_template_id, label_required: et.label_required }
        : x
    ))
    setEditingTransTempId(null)
  }

  function setInitial(tempId: string) {
    setStates(prev => prev.map(s => ({ ...s, is_initial: s.tempId === tempId })))
  }

  // ── Transition helpers ─────────────────────────────────────────────────────

  function addTransition() {
    if (!nt.from || !nt.to || !nt.action.trim() || nt.from === nt.to) return
    setTransitions(prev => [...prev, { tempId: uid(), from_temp_id: nt.from, to_temp_id: nt.to, action_name: nt.action.trim(), required_role_id: nt.required_role_id, auto_transition: nt.auto_transition, label_template_id: nt.label_template_id, label_required: nt.label_required }])
    setNt({ from: '', to: '', action: '', required_role_id: null, auto_transition: false, label_template_id: null, label_required: false })
    setShowAddTrans(false)
  }

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaveError(null)
    if (!name.trim())        { setSaveError('O nome do workflow é obrigatório'); return }
    if (states.length === 0) { setSaveError('Adiciona pelo menos um estado'); return }
    const initialState = states.find(s => s.is_initial)
    if (!initialState)       { setSaveError('Marca um estado como "Inicial" (clica em "○ Inicial")'); return }

    setSaving(true)
    try {
      let wfId: number

      if (isEdit && id) {
        // Update metadata
        await workflowsApi.update(Number(id), { name: name.trim(), description: description.trim() || undefined, category: category.trim() || undefined, is_active: isActive })
        wfId = Number(id)
        // Add only new states
        const idMap = new Map<string, number>(states.filter(s => s.serverId).map(s => [s.tempId, s.serverId!]))
        for (const s of states.filter(s => !s.serverId)) {
          const r = await workflowsApi.addState(wfId, { name: s.name, color: s.color, is_initial: s.is_initial, is_final: s.is_final, requires_approval: s.requires_approval, sla_hours: s.sla_hours !== '' ? Number(s.sla_hours) : undefined, assigned_role_id: s.assigned_role_id ?? undefined })
          idMap.set(s.tempId, r.data.id)
        }
        // Add only new transitions
        for (const t of transitions.filter(t => !t.serverId)) {
          const from = idMap.get(t.from_temp_id); const to = idMap.get(t.to_temp_id)
          if (from && to) await workflowsApi.addTransition(wfId, { from_state_id: from, to_state_id: to, action_name: t.action_name, required_role_id: t.required_role_id ?? undefined, auto_transition: t.auto_transition, label_template_id: t.label_template_id, label_required: t.label_required })
        }
        // Update initial state if changed
        const initId = idMap.get(initialState.tempId)
        if (initId) await workflowsApi.update(wfId, { initial_state_id: initId })

      } else {
        // Create
        const wfRes = await workflowsApi.create({ name: name.trim(), description: description.trim() || undefined, category: category.trim() || undefined })
        wfId = wfRes.data.id
        const idMap = new Map<string, number>()
        for (const s of states) {
          const r = await workflowsApi.addState(wfId, { name: s.name, color: s.color, is_initial: s.is_initial, is_final: s.is_final, requires_approval: s.requires_approval, sla_hours: s.sla_hours !== '' ? Number(s.sla_hours) : undefined, assigned_role_id: s.assigned_role_id ?? undefined })
          idMap.set(s.tempId, r.data.id)
        }
        for (const t of transitions) {
          const from = idMap.get(t.from_temp_id); const to = idMap.get(t.to_temp_id)
          if (from && to) await workflowsApi.addTransition(wfId, { from_state_id: from, to_state_id: to, action_name: t.action_name, required_role_id: t.required_role_id ?? undefined, auto_transition: t.auto_transition, label_template_id: t.label_template_id, label_required: t.label_required })
        }
        const initId = idMap.get(initialState.tempId)
        if (initId) await workflowsApi.update(wfId, { initial_state_id: initId, is_active: isActive })
      }

      navigate('/workflows')
    } catch {
      setSaveError('Erro ao guardar. Verifica os dados e tenta novamente.')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
    </div>
  )

  if (loadError) return (
    <div className="mt-6 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
      <AlertTriangle className="h-5 w-5" />{loadError}
    </div>
  )

  const nonFinalStates = states.filter(s => !s.is_final)

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/workflows')} className="btn btn-secondary mt-1 px-2 py-2">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Nome do workflow..."
            className="w-full max-w-xl bg-transparent text-2xl font-bold text-gray-900 border-0 border-b-2 border-transparent hover:border-gray-200 focus:border-primary-500 outline-none pb-0.5"
          />
          <p className="mt-0.5 text-sm text-gray-500">
            {isEdit ? `Editar workflow · ID ${id}` : 'Novo workflow'}
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary flex-shrink-0">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Guardar
        </button>
      </div>

      {saveError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />{saveError}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">

        {/* ── Left: Propriedades ── */}
        <div className="space-y-4">
          <div className="card">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Propriedades</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Descrição opcional..."
                  rows={3}
                  className="input h-auto w-full resize-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Categoria</label>
                <input
                  type="text"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  placeholder="Ex: NC, Auditoria, Qualidade..."
                  className="input w-full"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Ativo</span>
                <button
                  type="button"
                  onClick={() => setIsActive(v => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isActive ? 'bg-primary-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Summary box */}
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <GitBranch className="h-4 w-4 text-blue-600" />
              <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Resumo</h3>
            </div>
            <ul className="space-y-1 text-sm text-blue-800">
              <li>{states.length} estado(s)</li>
              <li>{transitions.length} transição(ões)</li>
              <li>{states.filter(s => s.requires_approval).length} requerem aprovação</li>
              <li className={states.find(s => s.is_initial) ? 'text-green-700' : 'text-amber-700'}>
                {states.find(s => s.is_initial) ? `Inicial: ${states.find(s => s.is_initial)!.name}` : '⚠ Estado inicial não definido'}
              </li>
            </ul>
          </div>
        </div>

        {/* ── Right: Estados + Transições ── */}
        <div className="lg:col-span-2 space-y-6">

          {/* ── Estados ── */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Estados <span className="ml-1 font-normal normal-case text-gray-400">({states.length})</span>
              </h2>
              <button onClick={() => setShowAddState(v => !v)} className="btn btn-secondary px-3 py-1.5 text-xs">
                <Plus className="mr-1 h-3 w-3" />Adicionar
              </button>
            </div>

            {states.length === 0 && !showAddState && (
              <p className="py-6 text-center text-sm text-gray-400">
                Nenhum estado criado. Clica em "Adicionar" para começar.
              </p>
            )}

            <div className="space-y-2">
              {states.map(s => (
                <div key={s.tempId} className="rounded-lg border border-gray-100 bg-gray-50">
                  {/* ── State row ── */}
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    {/* Color indicator */}
                    <div className="h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                    {/* Name */}
                    <span className="flex-1 truncate text-sm font-medium text-gray-900">{s.name}</span>
                    {/* Badges */}
                    <div className="hidden sm:flex flex-shrink-0 items-center gap-1.5">
                      {s.is_final && <span className="badge badge-success">Final</span>}
                      {s.requires_approval && (
                        <span className="badge badge-warning flex items-center gap-1">
                          <UserCheck className="h-3 w-3" />
                          {s.assigned_role_id ? (roles.find(r => r.id === s.assigned_role_id)?.name ?? `Role ${s.assigned_role_id}`) : 'Aprovação'}
                        </span>
                      )}
                      {s.sla_hours !== '' && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />{s.sla_hours}h
                        </span>
                      )}
                    </div>
                    {/* Set-initial toggle */}
                    <button
                      onClick={() => setInitial(s.tempId)}
                      title="Definir como estado inicial"
                      className={`flex-shrink-0 rounded border px-2 py-0.5 text-xs transition-colors ${
                        s.is_initial
                          ? 'border-blue-300 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500'
                      }`}
                    >
                      {s.is_initial ? '● Inicial' : '○ Inicial'}
                    </button>
                    {/* Edit */}
                    <button
                      onClick={() => editingStateTempId === s.tempId ? setEditingStateTempId(null) : startEditState(s)}
                      title="Editar estado"
                      className={`flex-shrink-0 transition-colors ${editingStateTempId === s.tempId ? 'text-primary-600' : 'text-gray-400 hover:text-primary-500'}`}
                    >
                      {editingStateTempId === s.tempId ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                    </button>
                    {/* Delete */}
                    <button
                      onClick={() => handleRemoveState(s.tempId)}
                      title="Eliminar estado"
                      className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* ── Inline edit form ── */}
                  {editingStateTempId === s.tempId && (
                    <div className="border-t border-primary-100 bg-primary-50 px-4 py-3 space-y-3 rounded-b-lg">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Nome *</label>
                          <input
                            autoFocus
                            type="text"
                            value={es.name}
                            onChange={e => setEs(p => ({ ...p, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveStateEdit() }}
                            className="input w-full text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Cor</label>
                          <select value={es.color} onChange={e => setEs(p => ({ ...p, color: e.target.value }))} className="input w-full text-sm">
                            {STATE_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">SLA (horas)</label>
                          <input
                            type="number" min={1}
                            value={es.sla_hours}
                            onChange={e => setEs(p => ({ ...p, sla_hours: e.target.value ? Number(e.target.value) : '' }))}
                            placeholder="Sem limite"
                            className="input w-full text-sm"
                          />
                        </div>
                        <div className="flex flex-col justify-end gap-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={es.is_final} onChange={e => setEs(p => ({ ...p, is_final: e.target.checked }))} className="rounded" />
                            Estado final
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={es.requires_approval} onChange={e => setEs(p => ({ ...p, requires_approval: e.target.checked }))} className="rounded" />
                            Requer aprovação
                          </label>
                        </div>
                        {es.requires_approval && (
                          <div className="sm:col-span-2">
                            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                              <UserCheck className="h-3 w-3" />Role de aprovação (opcional)
                            </label>
                            <select
                              value={es.assigned_role_id ?? ''}
                              onChange={e => setEs(p => ({ ...p, assigned_role_id: e.target.value ? Number(e.target.value) : null }))}
                              className="input w-full text-sm"
                            >
                              <option value="">Qualquer aprovador</option>
                              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingStateTempId(null)} className="btn btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
                        <button onClick={saveStateEdit} disabled={!es.name.trim()} className="btn btn-primary px-3 py-1.5 text-xs">
                          <Save className="mr-1 h-3 w-3" />Guardar Estado
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add-state inline form */}
            {showAddState && (
              <div className="mt-3 space-y-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
                <h3 className="text-sm font-semibold text-primary-800">Novo Estado</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Nome *</label>
                    <input
                      autoFocus
                      type="text"
                      value={ns.name}
                      onChange={e => setNs(p => ({ ...p, name: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') addState() }}
                      placeholder="Ex: Em Análise"
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Cor</label>
                    <select value={ns.color} onChange={e => setNs(p => ({ ...p, color: e.target.value }))} className="input w-full text-sm">
                      {STATE_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">SLA (horas)</label>
                    <input
                      type="number" min={1}
                      value={ns.sla_hours}
                      onChange={e => setNs(p => ({ ...p, sla_hours: e.target.value ? Number(e.target.value) : '' }))}
                      placeholder="Sem limite"
                      className="input w-full text-sm"
                    />
                  </div>
                  <div className="flex flex-col justify-end gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={ns.is_initial} onChange={e => setNs(p => ({ ...p, is_initial: e.target.checked }))} className="rounded" />
                      Estado inicial
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={ns.is_final} onChange={e => setNs(p => ({ ...p, is_final: e.target.checked }))} className="rounded" />
                      Estado final
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={ns.requires_approval} onChange={e => setNs(p => ({ ...p, requires_approval: e.target.checked }))} className="rounded" />
                      Requer aprovação
                    </label>
                  </div>
                  {ns.requires_approval && (
                    <div className="sm:col-span-2">
                      <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                        <UserCheck className="h-3 w-3" />Role de aprovação (opcional)
                      </label>
                      <select
                        value={ns.assigned_role_id ?? ''}
                        onChange={e => setNs(p => ({ ...p, assigned_role_id: e.target.value ? Number(e.target.value) : null }))}
                        className="input w-full text-sm"
                      >
                        <option value="">Qualquer aprovador</option>
                        {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAddState(false)} className="btn btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
                  <button onClick={addState} disabled={!ns.name.trim()} className="btn btn-primary px-3 py-1.5 text-xs">
                    <Plus className="mr-1 h-3 w-3" />Adicionar Estado
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Transições ── */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Transições <span className="ml-1 font-normal normal-case text-gray-400">({transitions.length})</span>
              </h2>
              <button
                onClick={() => setShowAddTrans(v => !v)}
                disabled={states.length < 2}
                title={states.length < 2 ? 'Precisa de pelo menos 2 estados' : undefined}
                className="btn btn-secondary px-3 py-1.5 text-xs disabled:opacity-40"
              >
                <Plus className="mr-1 h-3 w-3" />Adicionar
              </button>
            </div>

            {transitions.length === 0 && !showAddTrans && (
              <p className="py-6 text-center text-sm text-gray-400">
                Nenhuma transição definida. As transições determinam como o workflow avança entre estados.
              </p>
            )}

            <div className="space-y-2">
              {transitions.map(t => {
                const fromS = states.find(s => s.tempId === t.from_temp_id)
                const toS   = states.find(s => s.tempId === t.to_temp_id)
                return (
                  <div key={t.tempId} className="rounded-lg border border-gray-100 bg-gray-50">
                    {/* ── Transition row ── */}
                    <div className="flex items-center gap-2 px-3 py-2.5">
                      <div className="flex flex-1 min-w-0 items-center gap-2 text-sm">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <div className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: fromS?.color ?? '#6B7280' }} />
                          <span className="truncate font-medium text-gray-900">{fromS?.name ?? '?'}</span>
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="flex flex-col items-center gap-0.5">
                          <span className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
                            {t.action_name}
                          </span>
                          <span className="flex items-center gap-1">
                            {t.auto_transition && (
                              <span className="flex items-center gap-0.5 text-xs text-amber-600 font-medium">
                                <Zap className="h-2.5 w-2.5" />auto
                              </span>
                            )}
                            {t.required_role_id && (
                              <span className="flex items-center gap-0.5 text-xs text-purple-600">
                                <Shield className="h-2.5 w-2.5" />
                                {roles.find(r => r.id === t.required_role_id)?.name ?? `Role ${t.required_role_id}`}
                              </span>
                            )}
                            {t.label_template_id != null && (
                              <span className="flex items-center gap-0.5 text-xs text-teal-600">
                                <Tag className="h-2.5 w-2.5" />
                                {labelTemplates.find(l => l.id === t.label_template_id)?.name ?? 'Etiqueta'}
                              </span>
                            )}
                          </span>
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="flex items-center gap-1.5 min-w-0">
                          <div className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: toS?.color ?? '#6B7280' }} />
                          <span className="truncate font-medium text-gray-900">{toS?.name ?? '?'}</span>
                        </span>
                      </div>
                      {/* Edit */}
                      <button
                        onClick={() => editingTransTempId === t.tempId ? setEditingTransTempId(null) : startEditTrans(t)}
                        title="Editar transição"
                        className={`flex-shrink-0 transition-colors ${editingTransTempId === t.tempId ? 'text-primary-600' : 'text-gray-400 hover:text-primary-500'}`}
                      >
                        {editingTransTempId === t.tempId ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => handleRemoveTransition(t.tempId)}
                        title="Eliminar transição"
                        className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* ── Inline edit form ── */}
                    {editingTransTempId === t.tempId && (
                      <div className="border-t border-primary-100 bg-primary-50 px-4 py-3 space-y-3 rounded-b-lg">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">De (origem)</label>
                            <select value={et.from} onChange={e => setEt(p => ({ ...p, from: e.target.value }))} className="input w-full text-sm">
                              <option value="">Selecionar...</option>
                              {nonFinalStates.map(s => <option key={s.tempId} value={s.tempId}>{s.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Acção / botão *</label>
                            <input
                              autoFocus
                              type="text"
                              value={et.action}
                              onChange={e => setEt(p => ({ ...p, action: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveTransEdit() }}
                              placeholder="Ex: Aprovar, Rejeitar..."
                              className="input w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Para (destino)</label>
                            <select value={et.to} onChange={e => setEt(p => ({ ...p, to: e.target.value }))} className="input w-full text-sm">
                              <option value="">Selecionar...</option>
                              {states.filter(s => s.tempId !== et.from).map(s => <option key={s.tempId} value={s.tempId}>{s.name}</option>)}
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                              <Shield className="h-3 w-3" />Quem pode executar? (opcional)
                            </label>
                            <select
                              value={et.required_role_id ?? ''}
                              onChange={e => setEt(p => ({ ...p, required_role_id: e.target.value ? Number(e.target.value) : null }))}
                              className="input w-full text-sm"
                            >
                              <option value="">Qualquer utilizador</option>
                              {roles.map(r => <option key={r.id} value={r.id}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>)}
                            </select>
                          </div>
                          <div className="flex flex-col justify-end">
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input type="checkbox" checked={et.auto_transition} onChange={e => setEt(p => ({ ...p, auto_transition: e.target.checked }))} className="rounded" />
                              <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-amber-500" />Auto-transição</span>
                            </label>
                            <p className="mt-0.5 text-xs text-gray-400">Avança automaticamente ao entrar no estado origem.</p>
                          </div>
                          {labelTemplates.length > 0 && (
                            <div className="sm:col-span-2">
                              <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                                <Tag className="h-3 w-3" />Etiqueta a imprimir (opcional)
                              </label>
                              <select
                                value={et.label_template_id ?? ''}
                                onChange={e => setEt(p => ({ ...p, label_template_id: e.target.value ? Number(e.target.value) : null }))}
                                className="input w-full text-sm"
                              >
                                <option value="">Sem etiqueta</option>
                                {labelTemplates.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                              </select>
                            </div>
                          )}
                          {et.label_template_id != null && (
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input type="checkbox" checked={et.label_required} onChange={e => setEt(p => ({ ...p, label_required: e.target.checked }))} className="rounded" />
                                Etiqueta obrigatória
                              </label>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingTransTempId(null)} className="btn btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
                          <button onClick={saveTransEdit} disabled={!et.action.trim() || !et.from || !et.to} className="btn btn-primary px-3 py-1.5 text-xs disabled:opacity-50">
                            <Save className="mr-1 h-3 w-3" />Guardar Transição
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Add-transition inline form */}
            {showAddTrans && (
              <div className="mt-3 space-y-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
                <h3 className="text-sm font-semibold text-primary-800">Nova Transição</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">De (origem)</label>
                    <select value={nt.from} onChange={e => setNt(p => ({ ...p, from: e.target.value }))} className="input w-full text-sm">
                      <option value="">Selecionar...</option>
                      {nonFinalStates.map(s => <option key={s.tempId} value={s.tempId}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Acção / botão</label>
                    <input
                      type="text"
                      value={nt.action}
                      onChange={e => setNt(p => ({ ...p, action: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') addTransition() }}
                      placeholder="Ex: Aprovar, Rejeitar..."
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Para (destino)</label>
                    <select value={nt.to} onChange={e => setNt(p => ({ ...p, to: e.target.value }))} className="input w-full text-sm">
                      <option value="">Selecionar...</option>
                      {states.filter(s => s.tempId !== nt.from).map(s => <option key={s.tempId} value={s.tempId}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-3">
                    <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                      <Shield className="h-3 w-3" />Quem pode fazer esta transição? (opcional)
                    </label>
                    <select
                      value={nt.required_role_id ?? ''}
                      onChange={e => setNt(p => ({ ...p, required_role_id: e.target.value ? Number(e.target.value) : null }))}
                      className="input w-full text-sm"
                    >
                      <option value="">Qualquer utilizador</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>)}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">Se definido, só utilizadores com este papel podem executar esta acção.</p>
                  </div>
                  <div className="sm:col-span-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={nt.auto_transition} onChange={e => setNt(p => ({ ...p, auto_transition: e.target.checked }))} className="rounded" />
                      <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-amber-500" />Auto-transição</span>
                    </label>
                    <p className="mt-0.5 text-xs text-gray-400">Se activo, o workflow avança automaticamente para o estado destino assim que uma submissão cria a instância neste estado.</p>
                  </div>
                  {labelTemplates.length > 0 && (
                    <div className="sm:col-span-2">
                      <label className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                        <Tag className="h-3 w-3" />Etiqueta a imprimir (opcional)
                      </label>
                      <select
                        value={nt.label_template_id ?? ''}
                        onChange={e => setNt(p => ({ ...p, label_template_id: e.target.value ? Number(e.target.value) : null }))}
                        className="input w-full text-sm"
                      >
                        <option value="">Sem etiqueta</option>
                        {labelTemplates.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                  )}
                  {nt.label_template_id != null && (
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={nt.label_required} onChange={e => setNt(p => ({ ...p, label_required: e.target.checked }))} className="rounded" />
                        Etiqueta obrigatória
                      </label>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAddTrans(false)} className="btn btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
                  <button
                    onClick={addTransition}
                    disabled={!nt.from || !nt.to || !nt.action.trim()}
                    className="btn btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
                  >
                    <Plus className="mr-1 h-3 w-3" />Adicionar Transição
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
